export const parseEducationString = (eduString) => {
    if (!eduString || typeof eduString !== 'string') return null;
    const match = eduString.match(/^(.+?)\s+(.+?)\s+\((.+?)\)(?::\s+(.*))?$/);
    if (match) {
        const [, degree, institution, duration, details] = match;
        const [startDate, endDate] = duration.split('–').map(d => d.trim());
        return {
            degree: degree.trim(),
            institution: institution.trim(),
            startDate: startDate || '',
            endDate: endDate || '',
            field: details ? details.split(';')[0]?.replace('Field of study', '').trim() : '',
            grade: details ? (details.match(/Final grade (.+?);/) || [])[1] || '' : '',
            details: details || ''
        };
    }
    return {
        degree: eduString,
        institution: '',
        startDate: '',
        endDate: '',
        field: '',
        grade: '',
        details: ''
    };
};


export const validateMandatoryFields = (section, data) => {
    const validationRules = {
        header: [
            { field: 'location', message: 'Current Location is required.' },
            { field: 'phone', message: 'Phone number is required.' },
            { field: 'gender', message: 'Gender is required.' },
            { field: 'date_of_birth', message: 'Date of birth is required.', validator: (val) => val && val !== "Not disclosed" },
            {
                field: 'languages',
                message: 'At least one language is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            },
            {
                field: 'nationality',
                message: 'Nationality is required.',
                validator: (val) => Array.isArray(val) ? val.length > 0 : !!val
            }
        ],
        career: [
            { field: 'preferred_location', message: 'Preferred location is required.' },
            { field: 'availability_notice_period', message: 'Availability to work is required.' }
        ],
        skills: [
            {
                field: 'skills',
                message: 'At least one key skill is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            }
        ],
        education: [
            {
                field: 'education',
                message: 'At least one education entry is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            }
        ],
        experience: [
            {
                field: 'work_experience',
                message: 'At least one experience entry is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            }
        ],
        sectorInfo: [
            { field: 'sector_name', message: 'Sector is required.' },
            {
                field: 'discipline_names',
                message: 'At least one technical discipline is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            },
            {
                field: 'project_phase',
                message: 'At least one project phase is required.',
                validator: (val) => Array.isArray(val) && val.length > 0
            }
        ]
    };

    const rules = validationRules[section];
    if (!rules) return { valid: true, message: '' };

    for (const rule of rules) {
        const value = data[rule.field];
        const isValid = rule.validator
            ? rule.validator(value)
            : value && value.toString().trim() !== '';

        if (!isValid) {
            return { valid: false, message: rule.message };
        }
    }

    // Specific validation for education entries
    if (section === 'education' && Array.isArray(data.education)) {
        for (let i = 0; i < data.education.length; i++) {
            const edu = typeof data.education[i] === 'string'
                ? parseEducationString(data.education[i])
                : data.education[i];

            if (!edu || !edu.degree?.trim()) {
                return { valid: false, message: `Degree is required for education entry #${i + 1}.` };
            }
            if (!edu.institution?.trim()) {
                return { valid: false, message: `Institution is required for education entry #${i + 1}.` };
            }
        }
    }

    // Specific validation for experience entries
    if (section === 'experience' && Array.isArray(data.work_experience)) {
        for (let i = 0; i < data.work_experience.length; i++) {
            const exp = data.work_experience[i];
            if (!exp.title?.trim()) {
                return { valid: false, message: `Title is required for experience entry #${i + 1}.` };
            }
            if (!exp.company?.trim()) {
                return { valid: false, message: `Company is required for experience entry #${i + 1}.` };
            }
            if (!exp.duration?.trim() && (!exp.duration_text?.trim())) {
                return { valid: false, message: `Duration is required for experience entry #${i + 1}.` };
            }
        }
    }

    return { valid: true, message: '' };
};

export const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => deepClone(item));
    if (typeof obj === 'object') {
        const clonedObj = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                clonedObj[key] = deepClone(obj[key]);
            }
        }
        return clonedObj;
    }
};

export const transformApiResponse = (data, type) => {
    if (!Array.isArray(data)) return [];

    return data.map(item => {
        switch (type) {
            case 'languages':
                return {
                    value: item.code,
                    label: item.language,
                    id: item.code
                };
            case 'languageLevels':
                return {
                    value: item.code,
                    label: item.language,
                    id: item.code
                };
            case 'nationalities':
                return {
                    value: item.code,
                    label: item.name,
                    id: item.code
                };
            case 'sectors':
                return {
                    value: item.id,
                    label: item.name,
                    id: item.id
                };
            case 'subsectors':
                return {
                    value: item.id,
                    label: item.libelle,
                    id: item.id
                };
            case 'technicalDisciplines':
            case 'activities':
                return {
                    value: item.id || item.code,
                    label: item.name || item.libelle,
                    id: item.id || item.code
                };
            case 'projectPhases':
                return {
                    value: item.code,
                    label: item.name,
                    id: item.code,
                    name: item.name
                };
            default:
                return {
                    value: item.id || item.code,
                    label: item.name || item.libelle || item.language,
                    id: item.id || item.code
                };
        }
    });
};

export function resolveToOptionObj(raw, optionsList) {
    if (!raw || raw == 'do not disclose') return null;
    if (typeof raw === 'object' && raw.id !== undefined && raw.label !== undefined && raw.value !== undefined) {
        return raw; // already a correctly shaped object
    }
    let matched = null;
    if (typeof raw === 'object') {
        matched = optionsList && optionsList.find(
            opt =>
                (opt.id !== undefined && String(opt.id) === String(raw.id)) ||
                (opt.value !== undefined && String(opt.value) === String(raw.value)) ||
                (opt.label && raw.label && opt.label === raw.label)
        );
    } else {
        matched = optionsList && optionsList.find(
            opt =>
                (opt.id !== undefined && String(opt.id) === String(raw)) ||
                (opt.value !== undefined && String(opt.value) === String(raw)) ||
                (opt.label && opt.label === raw)
        );
    }
    if (matched) return matched;
    // Fallback: construct from whatever information is available
    const id = typeof raw === 'object'
        ? (raw.id !== undefined ? raw.id : (raw.value !== undefined ? raw.value : raw.label))
        : raw;
    const label = typeof raw === 'object'
        ? (raw.label !== undefined ? raw.label : (raw.name !== undefined ? raw.name : (raw.value !== undefined ? raw.value : raw.id)))
        : raw;
    const value = typeof raw === 'object'
        ? (raw.value !== undefined ? raw.value : (raw.id !== undefined ? raw.id : raw.label))
        : raw;
    if (id !== undefined && label !== undefined && value !== undefined) {
        return { id, label, value };
    }
    return null;
}
// Helper: for filling multi-select, for array [val1, val2, ...] => [{id, label, value}]
export function resolveToOptionObjArray(rawArr, optionsList) {
    if (!Array.isArray(rawArr)) return [];
    return rawArr.map(r => resolveToOptionObj(r, optionsList)).filter(Boolean);
}